import { formatTimestamp } from "src/utils/dateHandler.util";
import React from "react";
import {
  FiCalendar as Calendar,
  FiCreditCard as CreditCard,
  FiClock as Clock,
  FiDollarSign as DollarSign,
  FiHash as Hash,
  FiCheckCircle as CheckCircle,
  FiXCircle as XCircle,
  FiTag as Tag,
  FiUser as User,
} from "react-icons/fi";
import { Amount } from "src/components/Amount";
import { CreditVoucher } from "../../types/credit-voucher.entity";
import { CreditVoucherDetailsHeader } from "./header";
import { RedeemedUserInfo } from "./redeemedUser";
import { OrganizationInfo } from "./orgInfo";
import { IconType } from "react-icons";
import { VoucherQrCard } from "../voucher-qr-card";

type InfoItemProps = {
  label: string;
  text: React.ReactNode;
  icon: IconType;
};

const InfoItem = ({ label, text, icon: Icon }: InfoItemProps) => {
  return (
    <div className="flex items-start space-x-3">
      <Icon className="w-5 h-5 text-gray-500 mt-0.5" />
      <div className="text-left">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded">{text}</p>
      </div>
    </div>
  );
};

type CreditVoucherDetailsProps = {
  creditVoucher: CreditVoucher;
};

export const CreditVoucherDetails = ({ creditVoucher }: CreditVoucherDetailsProps) => {
  return (
    <>
      <CreditVoucherDetailsHeader />

      <div className="p-6 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <InfoItem icon={Hash} label="Voucher Code" text={creditVoucher.code} />
            <InfoItem
              icon={DollarSign}
              label="Amount"
              text={<Amount amount={creditVoucher.amount} currency={creditVoucher.currency} />}
            />
            <InfoItem
              icon={creditVoucher.status === "AVAILABLE" ? CheckCircle : XCircle}
              label="Status"
              text={creditVoucher.status}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {creditVoucher.remainingAmount ? (
              <InfoItem
                icon={DollarSign}
                label="Remaining Amount"
                text={<Amount amount={creditVoucher.remainingAmount} currency={creditVoucher.currency} />}
              />
            ) : null}
            <InfoItem icon={Tag} label="Type" text={creditVoucher.type} />
            <InfoItem icon={Calendar} label="Expiry Date" text={formatTimestamp(creditVoucher.expiryDate)} />
          </div>
          <div className="space-y-4">
            <InfoItem icon={Clock} label="Created At" text={formatTimestamp(creditVoucher.createdAt)} />

            {creditVoucher.redeemedBy && <InfoItem icon={User} label="Redeemed By" text={creditVoucher.redeemedBy} />}

            {creditVoucher.redeemedDate && (
              <InfoItem icon={Clock} label="Redeemed Date" text={formatTimestamp(creditVoucher.redeemedDate)} />
            )}

            {creditVoucher.paymentTransactionId && (
              <InfoItem icon={CreditCard} label="Transaction ID" text={creditVoucher.paymentTransactionId} />
            )}
          </div>
        </div>

        <VoucherQrCard code={creditVoucher.code} title="Voucher QR Code" size={156} />

        <RedeemedUserInfo redeemedBy={creditVoucher.redeemedBy} />
        <OrganizationInfo organizationId={creditVoucher.organizationId} />
      </div>
    </>
  );
};
